// red. HeroCardChart — der Vermögens-Verlauf IN der MemberCard, interaktiv.
// Wisch/Drag mit dem Finger über die Kurve → onScrub(value) feuert den Wert am
// angefahrenen Punkt; beim Loslassen onScrub(null) → der Hero springt auf den
// aktuellen Stand zurück. Ein dünner Koralle-Crosshair folgt dem Finger.
// touch-action:pan-y lässt vertikales Scrollen zu, horizontale Drags scrubben.
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';

const CORAL = '#E4544A';

// Downsample (≤44 Punkte) + quadratische Glättung — clean wie im Mock.
function buildModel(values, W, H, P, maxPts = 44) {
  let v = values;
  if (v.length > maxPts) {
    const stride = Math.ceil(v.length / maxPts);
    const out = [];
    for (let i = 0; i < v.length; i += stride) out.push(v[i]);
    if (out[out.length - 1] !== v[v.length - 1]) out.push(v[v.length - 1]);
    v = out;
  }
  const mn = Math.min(...v), mx = Math.max(...v), span = (mx - mn) || 1, n = v.length;
  const yAt = (val) => P + (1 - (val - mn) / span) * (H - 2 * P);
  const pts = v.map((val, i) => [n <= 1 ? 0 : (i / (n - 1)) * W, yAt(val)]);
  let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const mx2 = (pts[i - 1][0] + pts[i][0]) / 2, my2 = (pts[i - 1][1] + pts[i][1]) / 2;
    d += ` Q${pts[i - 1][0].toFixed(1)} ${pts[i - 1][1].toFixed(1)} ${mx2.toFixed(1)} ${my2.toFixed(1)}`;
  }
  d += ` L${pts[n - 1][0].toFixed(1)} ${pts[n - 1][1].toFixed(1)}`;
  return { d, pts, vals: v, yAt, mn, mx };
}

export default function HeroCardChart({
  values = [], line = CORAL, baseTone = 'rgba(0,0,0,0.18)', baselineValue, height = 88, onScrub,
  fillOpacity = 0.20, responsive = false,
}) {
  const baseW = 360, H = height, P = 8;
  const wrapRef = useRef(null);
  const idRef = useRef(`hc${Math.floor(Math.random() * 1e6)}`);
  const [hi, setHi] = useState(null);
  // responsive: viewBox-Breite = gemessene Pixel-Breite → 1:1-Skalierung, damit
  // Kreise (Jetzt-Punkt/Crosshair) auf breiten Desktop-Flächen RUND bleiben statt
  // zu Ellipsen gezogen zu werden. Default (Mobile) bleibt fix 360 + Stretch.
  const [measuredW, setMeasuredW] = useState(baseW);
  useEffect(() => {
    if (!responsive || !wrapRef.current) return undefined;
    const ro = new ResizeObserver(es => { for (const e of es) setMeasuredW(Math.max(120, e.contentRect.width)); });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [responsive]);
  const W = responsive ? measuredW : baseW;

  const model = useMemo(() => (values.length >= 2 ? buildModel(values, W, H, P) : null), [values, H, W]);

  const move = useCallback((clientX) => {
    if (!model) return;
    const el = wrapRef.current; if (!el) return;
    const rect = el.getBoundingClientRect(); if (rect.width <= 0) return;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const idx = Math.round(frac * (model.vals.length - 1));
    setHi(idx);
    onScrub?.(model.vals[idx]);
  }, [model, onScrub]);
  const clear = useCallback(() => { setHi(null); onScrub?.(null); }, [onScrub]);

  if (!model) return null;
  const id = idRef.current;
  const last = model.pts[model.pts.length - 1];
  const baseY = (typeof baselineValue === 'number')
    ? model.yAt(Math.max(model.mn, Math.min(model.mx, baselineValue))) : null;
  const hx = hi != null && model.pts[hi] ? model.pts[hi][0] : null;
  const hy = hi != null && model.pts[hi] ? model.pts[hi][1] : null;

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative', width: '100%', height: H, touchAction: 'pan-y', cursor: 'crosshair' }}
      onPointerDown={(e) => move(e.clientX)}
      onPointerMove={(e) => { if (e.buttons || e.pointerType === 'touch') move(e.clientX); }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
    >
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio={responsive ? 'xMidYMid meet' : 'none'} style={{ display: 'block' }} aria-hidden="true">
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={line} stopOpacity={fillOpacity} />
            <stop offset="1" stopColor={line} stopOpacity="0" />
          </linearGradient>
        </defs>
        {baseY != null && (
          <line x1="0" y1={baseY} x2={W} y2={baseY} stroke={baseTone} strokeWidth="1" strokeDasharray="2 6" opacity="0.7" />
        )}
        <path d={`${model.d} L ${W} ${H} L 0 ${H} Z`} fill={`url(#${id})`} />
        <path d={model.d} fill="none" stroke={line} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {hx != null ? (
          <>
            <line x1={hx} y1="0" x2={hx} y2={H} stroke={CORAL} strokeWidth="1" opacity="0.55" />
            <circle cx={hx} cy={hy} r="4.5" fill={CORAL} />
          </>
        ) : (
          <circle cx={Math.min(last[0], W - 4)} cy={last[1]} r="4" fill={CORAL} />
        )}
      </svg>
    </div>
  );
}
